import "./dialog.js";
import "./contact-form.js";
import "./countdown.js";
import { getActionText, getMedia } from "../utils/formatting.js";
import { getIcon } from "../utils/icons.js";
import { handleClick } from "../utils/action.js";

class SonikitPatch extends HTMLElement {
  #shadow

  constructor() {
    super();
    this.#shadow = this.attachShadow({ mode: 'closed' });
  }

  set patch(data) {
    this._patch = data;
    this.render();
  }

  async render() {
    let activeService;
    const defaultService = this._patch.services[0];
    const contactAdapter = this._patch.adapters.find(x => x.action === 'collect-contact');
    let contactDialog, contactForm, playerOptOut;

    let media;
    const mediaEnabled = this._patch.show_artwork || this._patch.show_title || this._patch.show_artist;
    if (mediaEnabled && defaultService) {
      let mediaData;
      if (defaultService.media_id && defaultService.media_type) {
        mediaData = await getMedia(sonikit.project._id, defaultService._id);
      }
      
      const imageUrl = mediaData?.image_url || defaultService.image_url;
      const title = mediaData?.title || defaultService.title;
      const artist = mediaData?.artist || defaultService.artist;
      
      media = `
        <div class="media" data-media-type=${defaultService.media_type}>
          ${this._patch.show_artwork ? `<div class="media__image-wrap"><img src=${imageUrl} /></div>` : ''}
          ${this._patch.show_title ? `<div class="media__title">${title}</div>` : ''}
          ${this._patch.show_artist ? `<div class="media__artist">${artist}</div>` : ''}
        </div>
      `
    }
    
    if (this._patch.action === 'player' &&  this._patch.allow_opt_out) {
      playerOptOut = `<button data-layout="text" data-role="player-opt-out">(or continue without music...)</button>`;
    }
    
    const template = document.createElement('template');
    template.innerHTML = `
      <style>
        .wrapper { 
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 10px;
          box-sizing: border-box;
          gap: 10px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .wrapper[data-layout="stacked"] {
          width: 100%;
        }
          
        .media {
          user-select: none;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          text-align: center;
        }
        .media__image-wrap {
          position: relative;
          width: ${this._patch.artwork_size ? this._patch.artwork_size : 150}px; 
          height: ${this._patch.artwork_size ? this._patch.artwork_size : 150}px;
          border-radius: 10px;
          overflow: hidden;
        }
        .media[data-media-type='artist'] .media__image-wrap,
        .media[data-media-type='user'] .media__image-wrap,
        .media[data-media-type='channel'] .media__image-wrap {
          border-radius: 50%;
        }
        .media__image-wrap img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .media__title {
          font-weight: 800;
        }
        .media__artist {
          opacity: 0.5;
        }
        .actions {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          vertical-align: middle;
          flex-wrap: wrap;
          user-select: none;
          box-sizing: border-box;
          gap: 10px;
          width: 100%;
        }
        .wrapper[data-layout="stacked"] .actions {
          flex-direction: column;
          width: 100%;
        }
        @media (min-width: 600px) {
          .actions {
            flex-direction: row;
            width: auto;
          }
        }
        button {
          display: flex;
          flex: 1 1 auto;
          justify-content: center;
          align-items: center;
          gap: 5px;
          text-align: center;
          text-transform: uppercase;
          font-weight: 700;
          box-sizing: border-box;
          padding: 8px 16px;
          border-radius: 50px;
          cursor: pointer;
          border: none;
          color: white;
          background-color: ${this._patch.button_fill === "default" ? "black" : this._patch.button_fill};
          user-select: none;
          width: 100%;
        }
        button, button:focus {
          outline: none;
        }
        @media (min-width: 600px) {
          button {
            width: auto;
          }
        }

        button[data-role="modal-control"] {
          width: 100%;
        }
        @media (min-width: 600px) {
          button[data-role="modal-control"] {
            width: 100%;
          }
        }

        .wrapper[data-layout="stacked"] button{
          width: 100%;
          max-width: 300px;
        }
        
        button svg {
          width: 1.2em;
          height: 1.2em;
          fill: currentColor;
          pointer-events: none;
        }

        button[data-layout='text'] {
          background-color: transparent;
          color: ${this._patch.button_text_color === "default" ? 'black' : this._patch.button_text_color};
          font-weight: 800;
          padding: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 4px;
          border: none;
          outline: none;
          cursor: pointer;
        }
        
        button[data-service="spotify"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#1ED760" : this._patch.button_fill};
        }
        button[data-service="apple-music"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#FF2E56" : this._patch.button_fill};
        }
        button[data-service="tidal"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "black" : this._patch.button_fill};
        }
        button[data-service="soundcloud"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#fd561f" : this._patch.button_fill};
        }
        button[data-service="deezer"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#B560FF" : this._patch.button_fill};
        }
        button[data-service="youtube"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#FF0100" : this._patch.button_fill};
        }
        button[data-service="amazon-music"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#4004F1" : this._patch.button_fill};
        }
        button[data-service="qobuz"] {
          color: ${this._patch.button_text_color === "default" ? 'white' : this._patch.button_text_color};
          background-color: ${this._patch.button_fill === "default" ? "#ffb12f" : this._patch.button_fill};
        }
      </style>
      <div class="wrapper" data-layout=${this._patch.layout}>
        ${media ? media : ''}
        <div class="actions"></div>
        ${playerOptOut ? playerOptOut : ''}
      </div>
    `;

    this.#shadow.appendChild(template.content.cloneNode(true));


    if (playerOptOut) {
      const optOutButton = this.#shadow.querySelector('[data-role="player-opt-out"]');
      optOutButton.addEventListener('click', () => {
        sonikit.emit('playerOptOut');
        if (this) this.remove();
      });
    }

    const actionWrapper = this.#shadow.querySelector('.actions');
    const modalActions = document.createElement('div');
    modalActions.classList = 'actions';

    const actionText = getActionText(this._patch.action, this._patch.release_date);
    this._patch.services.forEach(service => {
      const element = document.createElement('button');
      element.id = service._id;
      element.dataset.service = service.name;
      element.innerHTML = `${getIcon(service.name)} ${this._patch.action === 'player' ? service.display_name : actionText}`;
      element.addEventListener('click', (e) => {
        activeService = this._patch.services.find(x => x._id === e.target.id);
        if (contactAdapter) {
          contactForm.service = activeService;
          contactDialog.open(activeService);
        } else {
          handleClick(this._patch, service);
        }
      });

      if (this._patch.layout === 'modal') {
        modalActions.appendChild(element);
      } else {
        actionWrapper.appendChild(element);
      }
    });
    
    if (this._patch.layout === 'modal') {
      const actionDialog = document.createElement('sonikit-dialog');
      this.#shadow.querySelector('.wrapper').appendChild(actionDialog);
      actionDialog.render(this._patch.button_fill);
      actionDialog.setContent(modalActions);

      const actionDialogButton = document.createElement('button');
      actionDialogButton.dataset.role = 'modal-control';
      actionDialogButton.innerHTML = actionText;
      actionWrapper.appendChild(actionDialogButton)
      actionDialogButton.addEventListener('click', () => actionDialog.open());
    }
    
    let countdown;
    if (this._patch.action === 'presave' && this._patch.countdown_enabled) {
      countdown = document.createElement('sonikit-timer');
      countdown.patch = this._patch;
      const wrapper = this.#shadow.querySelector('.wrapper');
      const actionWrapper = this.#shadow.querySelector('.actions');
      wrapper.insertBefore(countdown, actionWrapper);
    }

    if (contactAdapter) {
      contactDialog = document.createElement('sonikit-dialog');
      this.#shadow.querySelector('.wrapper').appendChild(contactDialog);
      contactDialog.render();

      contactForm = document.createElement('sonikit-contact');
      contactForm.patch = contactAdapter;
      contactForm.parentPatch = this._patch;
      contactDialog.setContent(contactForm);
    }
  }
}

customElements.define('sonikit-patch', SonikitPatch);